import {useState} from 'react';

import {SimpleGrid} from '@mantine/core';

import {useOffers} from '../hooks/useOffers.ts';
import {useOffersTableColumns} from '../hooks/useOffersTableColumns.tsx';

import {
    DataTableTokenPagination,
    MantineDataTable,
    TableSearchForm,
    useTableSort,
} from '@flexinet/ui-components';

import {Offer} from '../types';
import {useAuth} from "../../../hooks/useAuth.ts";
import {useAllowedActions} from '../../../hooks/useAllowedActions.ts';
import {FormDrawer} from '../drawers/FormDrawer';
import {update} from '../services/OffersService';
import {stripReadonlyFields} from '../../../utils/stripReadonlyFields';
import {useQueryClient} from '@tanstack/react-query';
import {I18n} from '@aws-amplify/core';

export const RecordsTable = ({
                                 selectedRecords,
                                 setSelectedRecords,
                             }: {
    selectedRecords: Offer[],
    setSelectedRecords: (records: Offer[]) => void,
}) => {

    const {allowedActions} = useAllowedActions();
    const queryClient = useQueryClient();
    const [drawerOpened, setDrawerOpened] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState<Offer | null>(null);

    const {
        user,
    } = useAuth();

    if (!user) {
        throw new Error('user is required parameter');
    }

    const cognitoUsername = user.username;

    const {
        visibleItems,
        loading,
        nextToken,
        isFetchingMore,
        fetchRecords,
        handleFilterByKeyword,
    } = useOffers();

    // Newest first. `created` is the only creation timestamp Offer has; it is
    // nullable in the schema, so rows without it sort last in both directions.
    // Under token pagination this sorts only the pages fetched so far, not the
    // whole dataset: a property of the model, not a bug.
    const {sortStatus, setSortStatus, sorted} = useTableSort<Offer>(visibleItems, {
        columnAccessor: 'created',
        direction: 'desc',
    });

    const handleRecordClick = (record: Offer) => {
        setSelectedRecord(record);
        setDrawerOpened(true);
    };

    const columns = useOffersTableColumns(handleRecordClick);

    const handleDrawerSubmit = async (values: Offer) => {
        if (selectedRecord?.id) {
            await update(selectedRecord.id, stripReadonlyFields(values) as Offer);
            queryClient.invalidateQueries({queryKey: ['offers']});
        }
    };

    // Only the creator or manager of an offer may select it for deletion.
    const selectionProps = allowedActions.includes('DeleteOffer') ? {
        selectedRecords,
        onSelectedRecordsChange: setSelectedRecords,
        isRecordSelectable: (item: Offer) =>
            item.createdByUsername === cognitoUsername || item.managedByUsername === cognitoUsername,
    } : {};

    return (
        <SimpleGrid verticalSpacing='xl' m='sm'>
            <TableSearchForm handleFilterByKeyword={handleFilterByKeyword}/>

            <MantineDataTable<Offer>
                verticalSpacing='sm'
                striped
                highlightOnHover
                minHeight={150}
                records={sorted}
                columns={columns}
                fetching={loading}
                sortStatus={sortStatus}
                onSortStatusChange={setSortStatus}
                noRecordsText={I18n.get('No records found')}
                {...selectionProps}
            />

            <DataTableTokenPagination
                loadedCount={visibleItems.length}
                nextToken={nextToken}
                handleLoadMore={fetchRecords}
                isFetchingMore={isFetchingMore}
                noun='offer'
            />

            <FormDrawer
                record={selectedRecord || {} as Offer}
                handleSubmit={handleDrawerSubmit}
                opened={drawerOpened}
                onClose={() => setDrawerOpened(false)}
            />
        </SimpleGrid>
    );
};
